import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { trackPageView } from '../../firebase/trackingService';
import SEO from '../common/SEO';
import VinInput from '../car-check/VinInput';
import CarAnalysis from '../car-check/CarAnalysis';

const PageContainer = styled.section`
  background-color: #121212;
  color: #FFFFFF;
  min-height: 100vh;
  padding: 120px 24px 80px;
  
  @media (max-width: 768px) {
    padding: 100px 24px 60px;
  }
`;

const Content = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 42px;
  color: #2196F3;
  margin-bottom: 16px;
  font-weight: 700;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 48px;
  font-size: 18px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 32px;
  }
`;

const CheckCar = () => {
  const [vehicleData, setVehicleData] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    trackPageView('Check Car', window.location.pathname);
  }, []);

  const handleVinSubmit = async (vin) => {
    setLoading(true);
    setError(null);
    setVehicleData(null);
    setAnalysisData(null);

    try {
      // Fetch vehicle data from NHTSA API
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/decodevin/${vin}?format=json`);
      const data = await response.json();
      
      if (data.Results) {
        // Process NHTSA data
        const processedData = processNHTSAData(data.Results);
        setVehicleData(processedData);
        
        // Generate analysis using Cloud Function with Gemini AI
        const analysis = await generateCarAnalysis(processedData);
        setAnalysisData(analysis);
      } else {
        setError('Invalid VIN or no data found');
      }
    } catch (err) {
      setError('Failed to fetch vehicle data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const processNHTSAData = (results) => {
    const dataMap = {};
    results.forEach(item => {
      if (item.Value) {
        dataMap[item.Variable] = item.Value;
      }
    });

    return {
      make: dataMap['Make'] || 'Unknown',
      model: dataMap['Model'] || 'Unknown',
      year: dataMap['Model Year'] || 'Unknown',
      trim: dataMap['Trim'] || '',
      engineSize: dataMap['Displacement (L)'] || dataMap['Displacement (CC)'] || 'Unknown',
      cylinders: dataMap['Engine Number of Cylinders'] || 'Unknown',
      fuelType: dataMap['Fuel Type - Primary'] || 'Unknown',
      transmission: dataMap['Transmission Style'] || 'Unknown',
      driveType: dataMap['Drive Type'] || 'Unknown',
      bodyClass: dataMap['Body Class'] || 'Unknown',
      vehicleType: dataMap['Vehicle Type'] || 'Unknown',
      plantCountry: dataMap['Plant Country'] || 'Unknown',
      manufacturer: dataMap['Manufacturer Name'] || 'Unknown',
      doors: dataMap['Doors'] || 'Unknown',
      turbo: dataMap['Turbo'] || 'No'
    };
  };

  const generateCarAnalysis = async (vehicleData) => {
    try {
      // Call Cloud Function API
      const response = await fetch('https://us-central1-carconsul-web-b1649.cloudfunctions.net/analyzeVehicle', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ vehicleData })
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        return result.analysis;
      } else {
        throw new Error('Analysis failed');
      }
    } catch (error) {
      // Fallback to local analysis if Cloud Function fails
      return generateFallbackAnalysis(vehicleData);
    }
  };

  const generateFallbackAnalysis = (vehicleData) => {
    const { make, year, engineSize, transmission } = vehicleData;
    const currentYear = new Date().getFullYear();
    const age = currentYear - parseInt(year);
    
    // Fallback reliability scoring
    let reliability = 8.5;
    if (make.toLowerCase().includes('honda') || make.toLowerCase().includes('toyota')) {
      reliability = Math.max(7, 9.5 - (age * 0.3));
    } else if (make.toLowerCase().includes('bmw') || make.toLowerCase().includes('mercedes')) {
      reliability = Math.max(6, 8 - (age * 0.4));
    } else {
      reliability = Math.max(5, 8.5 - (age * 0.35));
    }

    let performance = 7;
    if (engineSize && parseFloat(engineSize) > 3.0) performance = 8.5;
    if (vehicleData.turbo === 'Yes') performance += 1;
    if (transmission.toLowerCase().includes('manual')) performance += 0.5;

    let fuelEconomy = 25;
    if (engineSize) {
      const displacement = parseFloat(engineSize);
      fuelEconomy = Math.max(15, 35 - (displacement * 5));
    }
    if (transmission.toLowerCase().includes('cvt')) fuelEconomy += 3;

    const depreciationData = generateDepreciationData(parseInt(year), 35000);

    return {
      scores: {
        reliability: Math.min(10, reliability),
        performance: Math.min(10, performance),
        fuelEconomy: Math.min(10, (fuelEconomy / 40) * 10),
        totalCostOfOwnership: Math.max(4, 9 - (age * 0.5)),
        resaleValue: Math.max(3, 8 - (age * 0.6)),
        maintenanceCost: Math.max(4, 8.5 - (age * 0.4))
      },
      fuelEconomyMPG: Math.round(fuelEconomy),
      estimatedMileage: age * 12000 + Math.random() * 5000,
      depreciationData,
      potentialIssues: generatePotentialIssues(vehicleData),
      buyingChecklist: generateBuyingChecklist(vehicleData),
      recalls: generateRecallInfo(vehicleData)
    };
  };

  const generateDepreciationData = (year, initialPrice) => {
    const currentYear = new Date().getFullYear();
    const data = [];
    
    for (let i = 0; i <= 10; i++) {
      const projectedYear = currentYear + i;
      const age = projectedYear - year;
      
      // Typical car depreciation: 20% first year, then 15% per year, slowing down
      let depreciationRate = 0;
      if (age === 1) depreciationRate = 0.20;
      else if (age <= 5) depreciationRate = 0.15;
      else if (age <= 10) depreciationRate = 0.10;
      else depreciationRate = 0.05;
      
      const retainedValue = Math.max(0.1, Math.pow(1 - depreciationRate, age));
      const currentValue = Math.round(initialPrice * retainedValue);
      
      data.push({
        year: projectedYear,
        value: currentValue,
        age: age
      });
    }
    
    return data;
  };

  const generatePotentialIssues = (vehicleData) => {
    const issues = [];
    const { make, year, transmission, engineSize } = vehicleData;
    const age = new Date().getFullYear() - parseInt(year);

    if (age > 8) {
      issues.push('Potential electrical system issues due to age');
      issues.push('Suspension components may need replacement');
    }
    
    if (transmission.toLowerCase().includes('cvt')) {
      issues.push('CVT transmission may require fluid changes every 30k miles');
    }
    
    if (make.toLowerCase().includes('bmw') || make.toLowerCase().includes('mercedes')) {
      issues.push('Higher maintenance costs typical for luxury vehicles');
      issues.push('Specialized parts and service requirements');
    }
    
    if (engineSize && parseFloat(engineSize) > 4.0) {
      issues.push('Higher fuel consumption and maintenance costs');
    }

    return issues;
  };

  const generateBuyingChecklist = (vehicleData) => {
    return [
      {
        category: 'Documentation',
        items: [
          'Verify clean title and VIN match',
          'Check maintenance records',
          'Confirm all recalls completed',
          'Review CarFax/AutoCheck report'
        ]
      },
      {
        category: 'Mechanical Inspection',
        items: [
          'Engine: Check for leaks, unusual noises',
          'Transmission: Smooth operation, proper fluid',
          'Brakes: Pad thickness, rotor condition',
          'Suspension: No leaks, proper alignment'
        ]
      },
      {
        category: 'Electrical & Interior',
        items: [
          'All lights and electrical components work',
          'Air conditioning and heating function',
          'Test all buttons, switches, and displays',
          'Check seat wear and interior condition'
        ]
      },
      {
        category: 'Test Drive',
        items: [
          'Engine starts easily and idles smoothly',
          'Transmission shifts properly',
          'Steering responsive, no pulling',
          'Brakes engage smoothly without noise'
        ]
      }
    ];
  };

  const generateRecallInfo = (vehicleData) => {
    // This would typically query NHTSA recall database
    return {
      hasRecalls: Math.random() > 0.7, // 30% chance of recalls for demo
      count: Math.floor(Math.random() * 3) + 1,
      description: 'Check NHTSA.gov with specific VIN for current recall information'
    };
  };

  return (
    <>
      <SEO 
        title="Check Car Before Buying - CarConsul"
        description="Get comprehensive vehicle analysis, reliability scores, and buying guidance before purchasing any car"
        pagePath="check-car-i-want-to-buy"
      />
      <PageContainer>
        <Content
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Title>Check Any Car Before Buying</Title>
          <Subtitle>
            Get instant vehicle analysis, reliability scores, potential issues, and a comprehensive buying checklist
          </Subtitle>

          <VinInput onSubmit={handleVinSubmit} loading={loading} error={error} />

          {vehicleData && analysisData && (
            <CarAnalysis 
              vehicleData={vehicleData} 
              analysisData={analysisData}
            />
          )}
        </Content>
      </PageContainer>
    </>
  );
};

export default CheckCar;